/**
 * Phase 2: React Hook for Real-time BoB Installation Progress
 *
 * Connects to SSE endpoint to receive live BoB installation updates.
 * Similar to useInstallationEvents but for BoB-specific installations.
 *
 * Events received:
 * - connected: Initial connection established
 * - progress_update: Download/installation progress (0-100%)
 * - status_change: BoB status changed (queued, downloading, installing, installed, failed)
 * - complete: Installation finished successfully
 * - error: Installation failed
 * - ping: Keepalive (ignored)
 *
 * Usage:
 * ```tsx
 * const { events, isConnected, error } = useBoBInstallationEvents(instanceId);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface BoBInstallationEvent {
  type: 'connected' | 'progress_update' | 'status_change' | 'complete' | 'error' | 'ping';
  timestamp: string;
  data?: {
    bob_name?: string;
    progress?: number;
    state?: string;
    bytes_downloaded?: number;
    total_bytes?: number;
    error?: string;
    message?: string;
  };
}

export interface UseBoBInstallationEventsResult {
  events: BoBInstallationEvent[];
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
}

const MAX_EVENTS = 200; // Higher limit for batch installations
const CONNECTION_TIMEOUT = 1800000; // 30 minutes (longer for large BoBs)
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 2000; // 2 seconds

export function useBoBInstallationEvents(instanceId: string | undefined): UseBoBInstallationEventsResult {
  const [events, setEvents] = useState<BoBInstallationEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const addEvent = useCallback((event: BoBInstallationEvent) => {
    setEvents((prev) => {
      const updated = [...prev, event];
      // Keep only last MAX_EVENTS to prevent memory leaks
      return updated.slice(-MAX_EVENTS);
    });
  }, []);

  const connectToSSE = useCallback(() => {
    if (!instanceId) {
      console.warn('No instanceId provided to useBoBInstallationEvents');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Clear existing timeouts
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    try {
      const url = `/api/instances/${instanceId}/bobs/install-events`;
      console.log(`Connecting to BoB installation SSE: ${url}`);

      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      // Connection timeout (30 minutes for large BoB downloads)
      connectionTimeoutRef.current = setTimeout(() => {
        console.warn('BoB installation SSE connection timeout (30 minutes), closing connection');
        eventSource.close();
        setError('Installation timeout - please check installation status manually');
        setIsConnected(false);
      }, CONNECTION_TIMEOUT);

      // Connection opened
      eventSource.addEventListener('open', () => {
        console.log('BoB installation SSE connection opened');
        setIsConnected(true);
        setError(null);
        setReconnectAttempts(0);

        addEvent({
          type: 'connected',
          timestamp: new Date().toISOString(),
        });
      });

      // Connected event (server confirmation)
      eventSource.addEventListener('connected', (e: MessageEvent) => {
        console.log('BoB installation SSE: connected event received');
        addEvent({
          type: 'connected',
          timestamp: new Date().toISOString(),
          data: JSON.parse(e.data),
        });
      });

      // Progress update event
      eventSource.addEventListener('progress_update', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        console.log('BoB installation progress:', data);

        addEvent({
          type: 'progress_update',
          timestamp: new Date().toISOString(),
          data,
        });
      });

      // Status change event
      eventSource.addEventListener('status_change', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        console.log('BoB installation status change:', data);

        addEvent({
          type: 'status_change',
          timestamp: new Date().toISOString(),
          data,
        });
      });

      // Complete event
      eventSource.addEventListener('complete', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        console.log('BoB installation complete:', data);

        addEvent({
          type: 'complete',
          timestamp: new Date().toISOString(),
          data,
        });
      });

      // Error event
      eventSource.addEventListener('error', (e: MessageEvent) => {
        // Check if it's a real error with data or just connection close
        if (e.data) {
          const data = JSON.parse(e.data);
          console.error('BoB installation error:', data);

          addEvent({
            type: 'error',
            timestamp: new Date().toISOString(),
            data,
          });

          setError(data.error || 'Installation error occurred');
        }
      });

      // Ping/keepalive event (ignored)
      eventSource.addEventListener('ping', () => {
        // Keepalive, no action needed
      });

      // Connection error (network issue, server down, etc.)
      eventSource.onerror = (e) => {
        console.error('BoB installation SSE connection error:', e);
        setIsConnected(false);

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAY * Math.pow(2, reconnectAttempts);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);

          setReconnectAttempts((prev) => prev + 1);

          reconnectTimeoutRef.current = setTimeout(() => {
            connectToSSE();
          }, delay);
        } else {
          console.error(`Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
          setError('Connection lost. Please refresh the page or check your network.');
          eventSource.close();
        }
      };
    } catch (err) {
      console.error('Error creating BoB installation SSE connection:', err);
      setError('Failed to establish connection');
      setIsConnected(false);
    }
  }, [instanceId, reconnectAttempts, addEvent]);

  const reconnect = useCallback(() => {
    console.log('Manual reconnect triggered');
    setReconnectAttempts(0);
    setError(null);
    connectToSSE();
  }, [connectToSSE]);

  // Connect on mount and when instanceId changes
  useEffect(() => {
    if (instanceId) {
      connectToSSE();
    }

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        console.log('Closing BoB installation SSE connection');
        eventSourceRef.current.close();
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [instanceId, connectToSSE]);

  return {
    events,
    isConnected,
    error,
    reconnect,
  };
}
